import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getDefaultDocListColumns,
  defaultSortOrder,
  nlpDefaultSortOrder,
} from '../../components/eca-doc-list/eca-doc-list.util';
import { QUERY_TYPE } from '../../constants/constants';
import { GridFilterModel } from '@mui/x-data-grid-pro';
import { defaultFilterModel } from '../../components/eca-doc-list/EcaDocListGrid';
import { RootState } from '../store';

export interface ISortOrder {
  column: string;
  direction: string;
  name: string;
  label: string;
  dataType: string;
  UIDataType?: string;
}

type TaggingStatus = 'Completed' | 'Failed' | null;

interface ISortPayload {
  isNlpSearch: boolean;
  sortOrder: ISortOrder[];
}

interface IColumnsPayload {
  isNlpSearch: boolean;
  columns: any[];
}

export interface IDocListContext {
  clientId: string | number | null | undefined;
  projectId: string | number | null | undefined;
}

interface IEcaDocListState {
  newSearch: boolean;
  columnsList: any[];
  nlpColumnsList: any[];
  sortOrder: ISortOrder[];
  nlpSortOrder: ISortOrder[];
  docListContext?: IDocListContext;
  taggingStatus: TaggingStatus;
  queryData: IQueryData;
  filterList: GridFilterModel;
  viewDocName: string;
  editDocsName?: string;
}

interface IQueryData {
  type: string;
  jsonLogicQuery: string; //json query from query builder
  dtSyntaxQuery: string;
  noScopeDtSyntaxQuery: string;
  relationalGroupFieldList: number[];
  scope?: {
    Type: string;
    Id: string;
    Name: string;
  };
  queryBuilderMode: boolean;
}

export const initialQueryData = {
  type: QUERY_TYPE.DT_SEARCH,
  jsonLogicQuery: '',
  dtSyntaxQuery: '',
  relationalGroupFieldList: [],
  queryBuilderMode: false,
  noScopeDtSyntaxQuery: '',
};

const initialState: IEcaDocListState = {
  newSearch: true,
  columnsList: getDefaultDocListColumns(),
  nlpColumnsList: getDefaultDocListColumns(),
  sortOrder: defaultSortOrder,
  nlpSortOrder: nlpDefaultSortOrder,
  docListContext: undefined,
  taggingStatus: null,
  queryData: initialQueryData,
  filterList: defaultFilterModel,
  viewDocName: '',
  editDocsName: '',
};

const ecaDocListSlice = createSlice({
  name: 'ecaDocListSlice',
  initialState,
  reducers: {
    setNewSearch(state, action: PayloadAction<boolean>) {
      state.newSearch = action.payload;
      if (action.payload == true) {
        state.columnsList = [
          ...getDefaultDocListColumns().filter(
            (col) => col.name != 'confidence_score'
          ),
        ];
        state.sortOrder = [...defaultSortOrder];
        state.filterList = { ...defaultFilterModel };
      }
    },
    resetDocListForContext(state, action: PayloadAction<IDocListContext>) {
      state.columnsList = getDefaultDocListColumns();
      state.sortOrder = [...defaultSortOrder];
      state.filterList = { ...defaultFilterModel };
      state.docListContext = action.payload;
    },
    removeConfidenceColumn(state) {
      state.columnsList = [
        ...getDefaultDocListColumns().filter(
          (col) => col.name != 'confidence_score'
        ),
      ];
    },
    setColumnsList(state, action: PayloadAction<IColumnsPayload>) {
      if (action.payload.isNlpSearch) {
        state.nlpColumnsList = action.payload.columns;
      } else {
        state.columnsList = action.payload.columns;
      }
    },
    setSortOrder(state, action: PayloadAction<ISortPayload>) {
      if (action.payload.isNlpSearch) {
        state.nlpSortOrder = action.payload.sortOrder;
      } else {
        state.sortOrder = action.payload.sortOrder;
      }
    },
    setTaggingStatus(state, action: PayloadAction<TaggingStatus>) {
      state.taggingStatus = action.payload;
    },
    setQueryData(state, action: PayloadAction<IQueryData>) {
      state.queryData = action.payload;
    },
    setFilterList(state, action: PayloadAction<GridFilterModel>) {
      state.filterList = action.payload;
    },
    setViewDocName(state, action: PayloadAction<string>) {
      state.viewDocName = action.payload;
    },
    setEditDocsName(state, action: PayloadAction<string>) {
      state.editDocsName = action.payload;
    },
    setDTSyntaxQuery(state, action: PayloadAction<string>) {
      state.queryData.dtSyntaxQuery = action.payload;
    },
  },
  selectors: {
    getColumnsList: (state) => state.columnsList,
    getNlpColumnsList: (state) => state.nlpColumnsList,
  }
});

export const {
  setNewSearch,
  resetDocListForContext,
  setColumnsList,
  setSortOrder,
  setTaggingStatus,
  setQueryData,
  setFilterList,
  setViewDocName,
  setEditDocsName,
  removeConfidenceColumn,
  setDTSyntaxQuery,
} = ecaDocListSlice.actions;

export const { getColumnsList, getNlpColumnsList } = ecaDocListSlice.getSelectors((state: RootState) => state.ecaDocList);

export default ecaDocListSlice.reducer;
